// @flow
import React from "react"
import { connect } from "react-redux"
import Cookies from "js-cookie"
import styled from "styled-components"
import type { Dispatch } from "redux"
import Text from "@kiwicom/orbit-components/lib/Text"
import InputField from "@kiwicom/orbit-components/lib/InputField"
import Button from "@kiwicom/orbit-components/lib/Button"
import Stack from "@kiwicom/orbit-components/lib/Stack"
import * as cookies from "@kiwicom/nitro/lib/services/session/cookies"
import { USER_ID } from "@kiwicom/nitro/lib/consts/cookies"

import { setOption } from "common/services/options/actions"
import { setNotice } from "common/services/notice"
import RadioButtons from "common/components/RadioButtons/RadioButtons"
import SmartFillOptionsList from "./SmartFillOptionsList"
import OptionToggleBtns from "./OptionToggleBtns"
import SetBrand from "./SetBrand"
import SaveLoadSplitster from "./SaveLoadSplitster"

export const FlexWrapper = styled.div`
  display: flex;
`

const ButtonWrapper = styled.div`
  margin: auto 0 0 0;
`

const TextWrapper = styled.div`
  width: 50%;
`

const toggleButtonsOptions = [
  {
    value: "enabled",
    label: "Enable",
  },
  {
    value: "disabled",
    label: "Disable",
  },
]

const smartFillButtonsOptions = [
  {
    value: "enabled",
    label: "Show",
  },
  {
    value: "disabled",
    label: "Hide",
  },
]

const paymentButtonsOptions = [
  {
    value: "default",
    label: "Default",
  },
  {
    value: "payu",
    label: "Pos",
  },
  {
    value: "zooz",
    label: "Tactus",
  },
]

const getPreProductionToggleOptions = () => [
  {
    value: "false",
    label: "Production",
  },
  {
    value: "true",
    label: "Pre-Production",
  },
]

type Props = {
  showSmartFillOptions: boolean,
  showConsoleLogs: string,
  fakeCuckoo: string,
  debugUmbrella: string,
  showReduxLogs: string,
  paymentMethodType: string,
  dispatch: Dispatch<any>,
  trackingIframeVisible: boolean,
}

type State = {
  affilId?: string,
}

class DebugTools extends React.Component<Props, State> {
  constructor(props) {
    super(props)
    this.state = {}
  }

  handleSubmit = () => {
    ;["affiliateThisSession", "affiliate"].forEach(item =>
      this.props.dispatch(setOption(item, this.state.affilId)),
    )
    this.setState({ affilId: "" })
    this.props.dispatch(
      setNotice({
        id: "affilIdSet",
        type: "success",
        title: "Success",
        ttl: 3000,
      }),
    )
  }

  handleClear = () => {
    const { dispatch } = this.props
    this.setState({ affilId: "" })
    dispatch(setOption("affiliateThisSession", ""))

    const defaultSettings = [
      {
        option: "enableHiddenFeatures",
        defaultValue: false,
      },
      {
        option: "showTranslationKeys",
        defaultValue: false,
      },
      {
        option: "showConsoleLogs",
        defaultValue: false,
        saveToCookies: true,
      },
      {
        option: "showReduxLogs",
        defaultValue: false,
        saveToCookies: true,
      },
      {
        option: "debugUmbrella",
        defaultValue: false,
        saveToCookies: true,
      },
      {
        option: "fakeCuckoo",
        defaultValue: false,
        saveToCookies: true,
      },
      {
        option: "trackingIframeVisible",
        defaultValue: false,
      },
      {
        option: "paymentMethodType",
        defaultValue: "default",
        saveToCookies: true,
      },
      {
        option: "showSmartFillOptions",
        defaultValue: false,
      },
    ]

    defaultSettings.forEach(item => {
      dispatch(setOption(item.option, item.defaultValue))
      if (item.saveToCookies) {
        Cookies.set(
          item.option === "fakeCuckoo" ? "debug_fakeCuckoo" : item.option,
          item.defaultValue,
        )
      }
    })

    this.props.dispatch(
      setNotice({
        id: "clearForm",
        type: "success",
        title: "Clear",
        ttl: 3000,
      }),
    )
  }

  handleInputChange = (e: SyntheticInputEvent<*>) => {
    const {
      target: { name, value },
    } = e
    this.setState({
      [name]: value,
    })
  }

  setPreProduction(newVersion) {
    // pre_production cookie tells nginx which Rancher port send request to (with true, requests are send to testing prod env without real traffic)
    Cookies.set("pre_production", newVersion, window.Skypicker.cookieOptions)
    window.location.reload(true)
  }

  renderPreReleaseToggle() {
    const preProductionVersion = Cookies.get("pre_production") || "false"

    return (
      <li className="DebugModal-ab-toggle-line" key="pre_production">
        <span>Frontend version: </span>
        <RadioButtons
          label="Frontend version: "
          modes={getPreProductionToggleOptions()}
          active={preProductionVersion}
          handleChange={this.setPreProduction}
        />
      </li>
    )
  }

  render() {
    const {
      showConsoleLogs,
      fakeCuckoo,
      debugUmbrella,
      showReduxLogs,
      paymentMethodType,
      dispatch,
      showSmartFillOptions,
      trackingIframeVisible,
    } = this.props

    return (
      <div>
        <h3>Dev tools</h3>
        {window.SP_GLOBALS.SKYPICKER_ENV === "production" &&
          !window.SP_GLOBALS.IS_STAGING &&
          this.renderPreReleaseToggle()}
        <ul className="DebugModal-buttons-list">
          <li>
            <OptionToggleBtns
              modes={toggleButtonsOptions}
              option="enableHiddenFeatures"
              label="Hidden features"
            />
          </li>
          <li>
            <OptionToggleBtns
              modes={toggleButtonsOptions}
              option="showTranslationKeys"
              label="Show translation keys"
            />
          </li>
          <li>
            <OptionToggleBtns
              modes={toggleButtonsOptions}
              option="showConsoleLogs"
              label="Show console logs"
              handleChange={() => Cookies.set("showConsoleLogs", !showConsoleLogs)}
              checkActive={optionState => optionState === "true" || optionState}
            />
          </li>
          <li>
            <OptionToggleBtns
              modes={toggleButtonsOptions}
              option="showReduxLogs"
              label="Show redux logs"
              handleChange={() => Cookies.set("showReduxLogs", !showReduxLogs)}
              checkActive={optionState => optionState === "true" || optionState}
            />
          </li>
          <li>
            <OptionToggleBtns
              modes={toggleButtonsOptions}
              option="debugUmbrella"
              label="Debug Umbrella"
              handleChange={() => Cookies.set("debugUmbrella", !debugUmbrella)}
              checkActive={optionState => optionState === "true" || optionState}
            />
          </li>
          <li>
            <OptionToggleBtns
              modes={toggleButtonsOptions}
              option="fakeCuckoo"
              label="Enable fake cuckoo (console instead of real tracking)"
              handleChange={() => Cookies.set("debug_fakeCuckoo", !fakeCuckoo)}
              checkActive={optionState => optionState === "true" || optionState}
            />
          </li>
          <li>
            <OptionToggleBtns
              modes={toggleButtonsOptions}
              option="trackingIframeVisible"
              label="Make tracking iframe visible"
              handleChange={() => {
                const style = trackingIframeVisible
                  ? "height: 0; visibility: hidden; position: absolute"
                  : "height: 80%; position: absolute; width: 40%; right: 0; z-index: 999999;"
                const iframe = ((document.querySelector("#trackingIframe"): any): HTMLIFrameElement)
                iframe.setAttribute("style", style)
              }}
              checkActive={optionState => optionState === "true" || optionState}
            />
          </li>

          <li>
            <Stack direction="row" justify="between">
              <TextWrapper>
                <Text type="primary">Payment method type: </Text>
              </TextWrapper>
              <RadioButtons
                modes={paymentButtonsOptions}
                active={paymentMethodType}
                handleChange={value => {
                  Cookies.set("paymentMethodType", value)
                  dispatch(setOption("paymentMethodType", value))
                }}
              />
            </Stack>
          </li>
          <li>
            <OptionToggleBtns
              modes={smartFillButtonsOptions}
              option="showSmartFillOptions"
              label="Smart fill options"
            />
          </li>
        </ul>
        <FlexWrapper>
          <InputField
            label="AffilId input"
            value={this.state.affilId}
            placeholder="Input affilId"
            name="affilId"
            onChange={this.handleInputChange}
          />
          <ButtonWrapper>
            <Button onClick={this.handleSubmit} block={false} type="primary" size="normal">
              Submit
            </Button>
          </ButtonWrapper>
        </FlexWrapper>

        <SetBrand />

        <Button onClick={this.handleClear}>Clear settings</Button>

        {showSmartFillOptions && <SmartFillOptionsList />}

        <SaveLoadSplitster />

        <h4>Visitor info</h4>
        <ul>
          <li>
            <strong>Visitor ID: </strong>
            <code>{String(cookies.load(USER_ID))}</code>
          </li>
          <li>
            <strong>Branch: </strong>
            <code>{String(window.SP_GLOBALS.RELEASE_INFO.branch)}</code>
          </li>
          <li>
            <strong>Affiliate: </strong>
            <code>{String(Cookies.get("SKYPICKER_AFFILIATE"))}</code>
          </li>
        </ul>

        <h4>Dependencies</h4>
        <ul>
          <li>
            <strong>Nitro: </strong>
            <code>{String(window.SP_GLOBALS.DEPENDENDCIES.nitro)}</code>
          </li>
          <li>
            <strong>Orbit: </strong>
            <code>{String(window.SP_GLOBALS.DEPENDENDCIES.orbit)}</code>
          </li>
          <li>
            <strong>Smart FAQ: </strong>
            <code>{String(window.SP_GLOBALS.DEPENDENDCIES.faq)}</code>
          </li>
        </ul>
      </div>
    )
  }
}

// $FlowFixMe TODO: plz add type when updating
export default connect(state => ({
  showSmartFillOptions: state.options.get("showSmartFillOptions"),
  showConsoleLogs: state.options.get("showConsoleLogs"),
  showReduxLogs: state.options.get("showReduxLogs"),
  debugUmbrella: state.options.get("debugUmbrella"),
  fakeCuckoo: state.options.get("fakeCuckoo"),
  paymentMethodType: state.options.get("paymentMethodType"),
  trackingIframeVisible: state.options.get("trackingIframeVisible"),
}))(DebugTools)
